import { useCallback, useEffect, useRef, useState } from 'react';

import crmOpportunityService from '../../domain/services/crm/crm.opportunity.service';
import { conversationHistoryTexts } from '../../domain/constant/crm/crm.conversation.constant';
import { describeOpenFailure } from './useOrbitaConversation';

export const historyStatuses = {
    loading: 'loading',
    empty: 'empty',
    ready: 'ready',
    error: 'error',
    sellerChanged: 'sellerChanged',
};

const embedSource = 'orbita-contact-history';
const gesttoSource = 'facilita-gestto-chat';

const getOrigin = (url) => {
    try {
        return new URL(url).origin;
    } catch (error) {
        return null;
    }
};

const useOrbitaContactHistory = (opportunityId, { isOpen = true } = {}) => {

    const [status, setStatus] = useState(historyStatuses.loading);
    const [history, setHistory] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);

    const frameRef = useRef(null);
    const isMountedRef = useRef(true);
    const shownOpportunityRef = useRef(null);

    useEffect(() => () => { isMountedRef.current = false; }, []);

    const fail = useCallback((message) => {
        setErrorMessage(message);
        setStatus(historyStatuses.error);
    }, []);

    const load = useCallback(async () => {
        if (!opportunityId)
            return;

        shownOpportunityRef.current = opportunityId;
        setHistory(null);
        setStatus(historyStatuses.loading);
        setErrorMessage(null);

        const isCurrent = () => isMountedRef.current && shownOpportunityRef.current === opportunityId;

        try {
            const loaded = await crmOpportunityService.findConversationHistory(opportunityId);

            if (!isCurrent())
                return;

            if (!loaded?.hasHistory) {
                setStatus(historyStatuses.empty);
                return;
            }

            if (!loaded.embedUrl || !loaded.accessToken) {
                fail(conversationHistoryTexts.loadFailed);
                return;
            }

            setHistory(loaded);
        } catch (error) {
            if (!isCurrent())
                return;

            const failure = describeOpenFailure(error, conversationHistoryTexts.loadFailed);
            setErrorMessage(failure.message);
            setStatus(failure.status);
        }
    }, [opportunityId, fail]);

    useEffect(() => {
        if (isOpen)
            load();
    }, [isOpen, load]);

    const embedUrl = history?.embedUrl || null;
    const accessToken = history?.accessToken || null;
    const embedOrigin = embedUrl ? getOrigin(embedUrl) : null;

    const embedSrc = embedUrl
        ? `${embedUrl}?parentOrigin=${encodeURIComponent(window.location.origin)}`
        : null;

    useEffect(() => {
        if (!embedOrigin || !accessToken)
            return undefined;

        const sendSession = () => frameRef.current?.contentWindow?.postMessage(
            {
                source: gesttoSource,
                type: 'facilita:session',
                payload: { token: accessToken },
            },
            embedOrigin
        );

        const handlers = {
            'orbita:ready': sendSession,
            'orbita:loaded': () => setStatus(historyStatuses.ready),
            'orbita:session-expired': () => fail(conversationHistoryTexts.expired),
            'orbita:error': () => fail(conversationHistoryTexts.loadFailed),
        };

        const handleEmbedMessage = (event) => {
            if (event.origin !== embedOrigin)
                return;

            const data = event.data || {};

            if (data.source !== embedSource || !handlers[data.type])
                return;

            handlers[data.type]();
        };

        window.addEventListener('message', handleEmbedMessage);

        return () => window.removeEventListener('message', handleEmbedMessage);
    }, [embedOrigin, accessToken, fail]);

    return {
        status,
        embedSrc,
        frameRef,
        errorMessage,
        reload: load,
    };
};

export default useOrbitaContactHistory;
